import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AssignmentLateRoundedIcon from "@mui/icons-material/AssignmentLateRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  DashboardCardEyebrow,
  DashboardCardHeader,
  DashboardCardState,
  DashboardPanelCard,
  DashboardRowLink,
  DashboardRowList,
  DashboardRowMarker,
  DashboardRowText,
} from "_shared/_views/dashboards/dashboard-card";
import { KeptTrail, KeptTrailLead, KeptTrailLine } from "_shared/_views/kept-trail/kept-trail-line";
import { ownIndividualProfileRoute } from "marketplace/_views/profiles/own-individual-profile-route";
import { useGetIndividualByIdQuery } from "marketplace/individuals/_api/individual-api";
import { areCredentialsFilledOut } from "marketplace/individuals/profiles/professional-credentials";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSession } from "users/__session/session-context";
import type { NetworkInvitationInfoJSON } from "users/networking/connections/invitations/_schemas/network-invitation-info-schema";
import NetworkInvitationQuery, {
  NetworkInvitationDirection,
  NetworkInvitationFilterParameters,
  NetworkInvitationOrderParameters,
} from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import { useGetUserNetworkInvitationsQuery } from "users/networking/connections/invitations/_api/network-invitation-api";
import {
  ProposalAction,
  ProposalContext,
  ProposalMove,
  ProposalOrderField,
  ProposalStage,
} from "work/proposals/__constants/proposal-lifecycle";
import { useGetProposalsQuery, useProposalListCounts } from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { proposalDestination, ProposalSection } from "work/proposals/_views/proposal-list-destination";
import { MOVE_PARAMETER, STAGE_PARAMETER, ProposalStageChip } from "work/proposals/_views/proposals-page-tabs";
import WorkVersion from "work/work-agreements/versions/work-version";

/**
 * The open invitations addressed to this user — the ones they sent are somebody else's move, and the
 * server leaves them out. A page this size is far past any real number of open invitations; if one
 * ever ran past it the count would read low rather than wrong, and the row still leads to the full list.
 */
const openNetworkInvitations = new NetworkInvitationQuery({
  filterParameters: new NetworkInvitationFilterParameters({
    direction: NetworkInvitationDirection.Received,
    status: "Open",
  }),
  orderParameters: new NetworkInvitationOrderParameters(),
  paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
});

/**
 * The three proposal queues the card counts, each as the proposal list's chips filter it (#6473).
 *
 * The card asks the API with the same stage and move, so a queue's count, its rows and the list its
 * overflow opens are one set. Counting the whole inbox here while the link opened Your move left a
 * reviewer's proposals out of the list they had just been counted in.
 */
const proposalQueues = {
  inbox: { stage: ProposalStage.Active, move: ProposalMove.YourMove },
  review: { move: ProposalMove.YourReview },
  authored: { stage: ProposalStage.Draft, move: ProposalMove.YourMove },
} as const;

export const proposalInboxRoute =
  `/proposals?${STAGE_PARAMETER}=${ProposalStageChip.Active}&${MOVE_PARAMETER}=${ProposalMove.YourMove}`;
export const proposalDraftReviewsRoute = `/proposals?${MOVE_PARAMETER}=${ProposalMove.YourReview}`;
export const proposalAuthoredDraftsRoute =
  `/proposals?${STAGE_PARAMETER}=${ProposalStageChip.Drafts}&${MOVE_PARAMETER}=${ProposalMove.YourMove}`;
export const receivedNetworkInvitationsRoute = "/network/pending?context=received";

export function networkInvitationRoute(invitationId: string): string {
  return `/network/pending/view/${invitationId}`;
}

/**
 * How many rows the card names before it starts counting instead.
 *
 * Four is what fits the card beside its neighbours in the grid without the queue becoming the
 * page. Anything past it is summarised per kind by the buttons underneath, which lead to the same
 * three lists this card used to be made of.
 */
export const dashboardWaitingLimit = 4;

export type WaitingKind =
  | "credentials"
  | "inboxProposal"
  | "draftReviewProposal"
  | "authoredDraftProposal"
  | "networkInvitation";

export type WaitingRow = {
  key: string;
  kind: WaitingKind;
  /** The thing itself: a proposal's name, the person who sent the invitation, or their own credentials. */
  title: string;
  /**
   * Which version of the work a proposal is, "v3 (proposed)" (#6636), so two proposals on one work
   * read apart. Absent from a row that is not a proposal, and from a payload older than the numbers.
   */
  version?: string;
  /** What is being asked of this user about it. */
  caption: string;
  to: string;
};

export type WaitingOverflow = {
  count: number;
  label: string;
  to: string;
};

function counted(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/**
 * What the proposal is actually asking of this user, read off the actions the server offers them.
 *
 * "Awaiting your response" was the only thing this card ever said, for every proposal in the inbox
 * and every unsent draft alike, which is how a reviewer's proposal came to read as though it were
 * waiting to be submitted rather than read (#5115). `availableActions` is the server's answer to
 * "what may this user do here", so the sentence and the destination are derived from one fact:
 * a row that opens the review flow says review, and a row that opens the draft editor says draft.
 *
 * The draft box it came from says the same thing a second way: `DraftReview` is the drafts this
 * user was asked to read, so a row out of it is a review whatever else the proposal offers.
 */
export function waitingCaption(kind: WaitingKind, offers: (action: ProposalAction) => boolean): string {
  if (kind === "credentials") return "Fill them in to join teams and the Marketplace";
  if (kind === "networkInvitation") return "Invited you to connect";
  if (kind === "draftReviewProposal" || offers(ProposalAction.Review)) return "Waiting for your review";
  if (kind === "authoredDraftProposal") {
    return offers(ProposalAction.Submit) ? "Ready to submit" : "Draft you have not submitted";
  }
  // Everything else in the inbox is the other party's move handed back, whether this user answers
  // it by revising, approving or rejecting.
  return "Waiting for your response";
}

function proposalRows(
  proposals: ProposalJSON[],
  kind: WaitingKind,
  section: ProposalSection | ((proposal: ProposalJSON) => ProposalSection),
  context: { userId: string | undefined; isRestrictedOnboarding: boolean },
): WaitingRow[] {
  return proposals.flatMap((proposal) => {
    // A row with no id has nowhere to go, and a queue row that is not a door is not worth a line.
    if (!proposal.id) return [];
    const offers = (action: ProposalAction) =>
      (proposal.availableActions?.[action] ?? []).some((userId) => userId === context.userId);
    return [{
      key: proposal.id,
      kind,
      title: proposal.name?.trim() || "Untitled proposal",
      version: WorkVersion.fromNumbers(proposal)?.label,
      caption: waitingCaption(kind, offers),
      to: proposalDestination(proposal.id, offers, {
        section: typeof section === "function" ? section(proposal) : section,
        isRestrictedOnboarding: context.isRestrictedOnboarding,
      }),
    }];
  });
}

/** The row's title as a reader tells it from the work's other versions: "10/13 Test · v3 (proposed)". */
function titleWithVersion(row: WaitingRow): string {
  return row.version ? `${row.title}${WorkVersion.SEPARATOR}${row.version}` : row.title;
}

function reviewSectionOf(proposal: ProposalJSON): ProposalSection {
  return proposal.status === "AwaitingSubmission" ? ProposalSection.Drafts : ProposalSection.Active;
}

/** For a row that is not a proposal, so nothing about it is an action the server offers. */
const offersNothing = () => false;

function invitationRows(invitations: NetworkInvitationInfoJSON[]): WaitingRow[] {
  return invitations.map((invitation) => ({
    key: invitation.id,
    kind: "networkInvitation" as const,
    title: [invitation.inviter?.firstName, invitation.inviter?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "Someone in your network",
    caption: waitingCaption("networkInvitation", offersNothing),
    to: networkInvitationRoute(invitation.id),
  }));
}

/**
 * The signed-in user's own profile, as far as the queue needs it. Absent when the card may not send
 * them there or does not yet know (see `WaitingOnYouCard`), and an absent profile is never a row.
 */
export type OwnProfile = {
  individualId: string;
  credentials: string | null | undefined;
};

/**
 * Their own credentials, when a vendor team would refuse them as they stand (#5800) and the Marketplace
 * would not show their profile (#6608). One row, not a count:
 * there is one profile, and filling it in is a thing only they can do.
 *
 * Not during restricted onboarding. The session is held to its onboarding proposal there, so the
 * profiles page is a route it would be turned away from, and the menu that leads to it is hidden.
 */
function credentialsRows(ownProfile: OwnProfile | undefined, isRestrictedOnboarding: boolean): WaitingRow[] {
  if (!ownProfile || isRestrictedOnboarding || areCredentialsFilledOut(ownProfile.credentials)) return [];
  return [{
    key: "credentials",
    kind: "credentials",
    title: "Your professional credentials",
    caption: waitingCaption("credentials", offersNothing),
    to: ownIndividualProfileRoute(ownProfile.individualId, { promptForCredentials: true }),
  }];
}

/**
 * Every open item, most pressing first: their own missing credentials, which stand between them and
 * every team they might be put on, then what the other party handed back, then the drafts somebody
 * asked this user to read, then their own unsent drafts, then the people waiting to be answered.
 * Below the credentials, the order is the one the card showed when it was counted lines, so a reader
 * who knew where to look still finds the same thing first.
 *
 * The credentials row leads so that the card's limit cannot push it off. Nothing would bring it
 * back if it did: no overflow button counts it, because there is no list of one to send it to.
 */
export function waitingRows(input: {
  ownProfile?: OwnProfile;
  inboxProposals: ProposalJSON[];
  draftReviewProposals: ProposalJSON[];
  authoredDraftProposals: ProposalJSON[];
  networkInvitations: NetworkInvitationInfoJSON[] | undefined;
  userId: string | undefined;
  isRestrictedOnboarding: boolean;
}): WaitingRow[] {
  const context = { userId: input.userId, isRestrictedOnboarding: input.isRestrictedOnboarding };
  return [
    ...credentialsRows(input.ownProfile, input.isRestrictedOnboarding),
    ...proposalRows(input.inboxProposals, "inboxProposal", ProposalSection.Active, context),
    // A review is asked of a draft or of an active proposal; each opens in its own section.
    ...proposalRows(input.draftReviewProposals, "draftReviewProposal", reviewSectionOf, context),
    ...proposalRows(input.authoredDraftProposals, "authoredDraftProposal", ProposalSection.Drafts, context),
    ...invitationRows(input.networkInvitations ?? []),
  ];
}

/**
 * What is left over once the card has named as many rows as it can hold, per kind.
 *
 * Counting per kind rather than in one number is what keeps the overflow honest: there is no page
 * that holds proposals, drafts and invitations together, so a single "+7 more" would have had
 * nowhere to lead. Each button goes to the list its own count came from — which is why the two
 * halves of the draft box count separately, the way the drafts tab now filters them (#5115).
 */
export function waitingOverflow(
  shown: WaitingRow[],
  totals: {
    inboxProposals: number;
    draftReviewProposals: number;
    authoredDraftProposals: number;
    networkInvitations: number | undefined;
  },
): WaitingOverflow[] {
  const shownOf = (kind: WaitingKind) => shown.filter((row) => row.kind === kind).length;
  const remaining = [
    {
      count: totals.inboxProposals - shownOf("inboxProposal"),
      one: "more proposal awaiting your response",
      many: "more proposals awaiting your response",
      to: proposalInboxRoute,
    },
    {
      count: totals.draftReviewProposals - shownOf("draftReviewProposal"),
      one: "more proposal awaiting your review",
      many: "more proposals awaiting your review",
      to: proposalDraftReviewsRoute,
    },
    {
      count: totals.authoredDraftProposals - shownOf("authoredDraftProposal"),
      one: "more draft you have not submitted",
      many: "more drafts you have not submitted",
      to: proposalAuthoredDraftsRoute,
    },
    {
      count: (totals.networkInvitations ?? 0) - shownOf("networkInvitation"),
      one: "more network invitation to answer",
      many: "more network invitations to answer",
      to: receivedNetworkInvitationsRoute,
    },
  ];
  // A user without network access is offered no invitation row at all, not a row reading zero.
  if (totals.networkInvitations === undefined) remaining.pop();

  return remaining
    .filter((kind) => kind.count > 0)
    .map((kind) => ({
      count: kind.count,
      label: `+${kind.count} ${counted(kind.count, kind.one, kind.many)}`,
      to: kind.to,
    }));
}

const waitingKindIcons: Record<WaitingKind, typeof AssignmentRoundedIcon> = {
  credentials: BadgeRoundedIcon,
  inboxProposal: AssignmentRoundedIcon,
  draftReviewProposal: RateReviewRoundedIcon,
  authoredDraftProposal: EditNoteRoundedIcon,
  networkInvitation: PersonAddAltRoundedIcon,
};

/**
 * The criteria the queues' one counts request sends: the reader's side and the Your move chip.
 * `proposalQueues` names each queue's stage and move for its rows and its link.
 */
function proposalQueueCountsCriteria(viewingAsVendor: boolean): string {
  return new ProposalFilterParameters({
    move: ProposalMove.YourMove,
    context: viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
  }).searchParams.toString();
}

function proposalPage(
  queue: { stage?: ProposalStage; move: ProposalMove },
  viewingAsVendor: boolean,
): ProposalQuery {
  return new ProposalQuery({
    filterParameters: new ProposalFilterParameters({
      ...queue,
      context: viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
    }),
    orderParameters: new ProposalOrderParameters({
      orderBy: ProposalOrderField.Created,
      orderDir: OrderDirection.Descending,
    }),
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: dashboardWaitingLimit }),
  });
}

/**
 * What is actually this user's move, rather than what is merely in flight. Each row is a thing the
 * platform is holding for them: the professional credentials no team will take them without, a
 * proposal the other party has handed back, a draft somebody asked them to read, a draft they have
 * not sent, an invitation nobody has answered. Nothing here is a notification about those things —
 * the bell already carries those, and counting both would count the same work twice.
 *
 * The rows name the proposal and open it, rather than counting a category and landing on a filtered
 * list (#5123). "3 proposals awaiting your response" told the reader nothing they could act on and
 * cost them two more clicks to find out; the name and the destination both come from the same list
 * response, so the row cannot promise something the proposal does not offer.
 *
 * The drafts arrive as the two boxes they are, rather than as the one box that holds both. A
 * requested reviewer joins the draft box of the party whose draft it is, so the box alone cannot
 * say whether a draft is this user's to send or somebody else's to read (#5115) — and the overflow
 * counts, which are the only category sentences left on the card, have to say which.
 *
 * This is the one card that keeps the loud marker, because every row on it is by definition the
 * reader's move.
 */
export default function WaitingOnYouCard() {
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  // Network is permission-gated at the route, so a user without it must not be offered the row.
  const includesNetwork = sessionPermissions?.includesAccessToNetwork === true;
  const viewingAsVendor = session.context?.viewingAsVendor === true;
  // The profiles page refuses a session without read/write marketplace access, so the credentials
  // row is only offered to one that has it, and only once the session knows which profile is theirs.
  // And only viewing as a vendor: a client team asks nothing of anybody's credentials, so to a client
  // the row would be a chore nothing is waiting on.
  const ownIndividualId = session.user?.individualId?.value;
  const asksForCredentials =
    ownIndividualId !== undefined &&
    viewingAsVendor &&
    !session.isRestrictedOnboarding &&
    sessionPermissions?.includesReadWriteAccessToMarketplace === true;

  const inboxProposalsQuery = useGetProposalsQuery({
    interfaceName: "dashboardWaitingOnYou",
    query: useMemo(() => proposalPage(proposalQueues.inbox, viewingAsVendor), [viewingAsVendor]),
  });
  const draftReviewProposalsQuery = useGetProposalsQuery({
    interfaceName: "dashboardWaitingOnYou",
    query: useMemo(() => proposalPage(proposalQueues.review, viewingAsVendor), [viewingAsVendor]),
  });
  const authoredDraftProposalsQuery = useGetProposalsQuery({
    interfaceName: "dashboardWaitingOnYou",
    query: useMemo(() => proposalPage(proposalQueues.authored, viewingAsVendor), [viewingAsVendor]),
  });
  // The three queues' counts are chips of one proposal list, so one counts request answers them
  // all (#6709): with Your move chosen, the Active and Draft chips count the inbox and the drafts
  // the reader authored, and the Your review chip, which ignores its own group's choice, counts
  // the drafts they were asked to read.
  const queueCounts = useProposalListCounts({ criteria: proposalQueueCountsCriteria(viewingAsVendor) });
  const inboxCount = queueCounts.counts?.stages[ProposalStage.Active];
  const draftReviewCount = queueCounts.counts?.moves[ProposalMove.YourReview];
  const authoredDraftCount = queueCounts.counts?.stages[ProposalStage.Draft];
  const networkInvitationsQuery = useGetUserNetworkInvitationsQuery(openNetworkInvitations, { skip: !includesNetwork });
  const ownIndividualQuery = useGetIndividualByIdQuery(
    { individualId: ownIndividualId ?? "" },
    { skip: !asksForCredentials },
  );
  const ownIndividual = asksForCredentials ? ownIndividualQuery.data : undefined;

  const networkInvitationCount = includesNetwork
    ? (networkInvitationsQuery.data?.data.length ?? 0)
    : undefined;

  const isLoading =
    inboxProposalsQuery.isLoading ||
    draftReviewProposalsQuery.isLoading ||
    authoredDraftProposalsQuery.isLoading ||
    queueCounts.isLoading ||
    networkInvitationsQuery.isLoading ||
    ownIndividualQuery.isLoading;
  // The profile is left out on purpose: the rest of the queue is worth showing without that one row,
  // and the profile page says for itself whether it can load.
  const isError =
    inboxProposalsQuery.isError ||
    draftReviewProposalsQuery.isError ||
    authoredDraftProposalsQuery.isError ||
    queueCounts.isError ||
    networkInvitationsQuery.isError;

  const rows = useMemo(
    () =>
      waitingRows({
        ownProfile: ownIndividual
          ? { individualId: ownIndividual.id, credentials: ownIndividual.credentials }
          : undefined,
        inboxProposals: inboxProposalsQuery.data?.data ?? [],
        draftReviewProposals: draftReviewProposalsQuery.data?.data ?? [],
        authoredDraftProposals: authoredDraftProposalsQuery.data?.data ?? [],
        networkInvitations: includesNetwork ? networkInvitationsQuery.data?.data : undefined,
        userId: session.user?.id?.value,
        isRestrictedOnboarding: session.isRestrictedOnboarding,
      }),
    [
      authoredDraftProposalsQuery.data,
      draftReviewProposalsQuery.data,
      inboxProposalsQuery.data,
      includesNetwork,
      networkInvitationsQuery.data,
      ownIndividual,
      session.isRestrictedOnboarding,
      session.user?.id?.value,
    ],
  );
  const visible = rows.slice(0, dashboardWaitingLimit);
  const overflow = waitingOverflow(visible, {
    inboxProposals: inboxCount ?? 0,
    draftReviewProposals: draftReviewCount ?? 0,
    authoredDraftProposals: authoredDraftCount ?? 0,
    networkInvitations: networkInvitationCount,
  });
  // The credentials row counts itself: no endpoint counts it, and there is only ever the one.
  const total =
    rows.filter((row) => row.kind === "credentials").length +
    (inboxCount ?? 0) +
    (draftReviewCount ?? 0) +
    (authoredDraftCount ?? 0) +
    (networkInvitationCount ?? 0);

  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <div>
          <DashboardCardEyebrow>
            <AssignmentLateRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Your queue</Typography>
          </DashboardCardEyebrow>
          <Typography variant="h5" component="h2">
            {total > 0 ? `${total} waiting on you` : "Waiting on you"}
          </Typography>
        </div>
      </DashboardCardHeader>
      {isLoading && (
        <DashboardCardState role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading what needs your response…</Typography>
        </DashboardCardState>
      )}
      {!isLoading && isError && (
        <DashboardCardState>
          <Typography color="error" variant="body2">What needs your response could not be loaded.</Typography>
        </DashboardCardState>
      )}
      {!isLoading && !isError && visible.length === 0 && (
        <DashboardCardState>
          <CheckCircleRoundedIcon color="success" fontSize="small" />
          <Typography variant="body2">Nothing is waiting on you.</Typography>
        </DashboardCardState>
      )}
      {!isLoading && !isError && visible.length > 0 && (
        <DashboardRowList>
          <Stack spacing={1}>
            {visible.map((row) => {
              const KindIcon = waitingKindIcons[row.kind];
              return (
                <DashboardRowLink key={row.key} to={row.to} aria-label={`${titleWithVersion(row)} — ${row.caption}`}>
                  <DashboardRowMarker aria-hidden="true">
                    <KindIcon fontSize="small" />
                  </DashboardRowMarker>
                  <DashboardRowText>
                    <KeptTrailLine variant="subtitle2">
                      <KeptTrailLead>{row.title}</KeptTrailLead>
                      {row.version && (
                        <KeptTrail>{`${WorkVersion.SEPARATOR}${row.version}`}</KeptTrail>
                      )}
                    </KeptTrailLine>
                    <Typography color="text.secondary" noWrap variant="caption">{row.caption}</Typography>
                  </DashboardRowText>
                  <ArrowForwardRoundedIcon color="action" fontSize="small" />
                </DashboardRowLink>
              );
            })}
          </Stack>
          {overflow.length > 0 && (
            // One element, so the list's "push the last child to the bottom" rule still lines this
            // card's controls up with the cards beside it however many kinds overflowed.
            <Box>
              {overflow.map((kind) => (
                <Button key={kind.to} component={RouterLink} to={kind.to} size="small">{kind.label}</Button>
              ))}
            </Box>
          )}
        </DashboardRowList>
      )}
    </DashboardPanelCard>
  );
}
